import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useNavigate, useParams } from "react-router-dom";

import {
  HiOutlineArrowLeft,
  HiOutlineBuildingOffice2,
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
  HiOutlineHashtag,
  HiOutlinePencilSquare,
  HiOutlineUserGroup,
  HiOutlineXCircle,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";

import usePermissions from "../../../hooks/usePermissions";

import { getOrganizationById } from "../../../redux/features/organizations/organizationSlice";

import "./OrganizationDetailsPage.css";

export default function OrganizationDetailsPage() {
  const { id } = useParams();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { can } = usePermissions();

  const { selectedOrganization, isLoading, error } = useSelector(
    (state) => state.organizations,
  );

  useEffect(() => {
    if (!id) return;

    dispatch(getOrganizationById(id));
  }, [dispatch, id]);

  if (isLoading) {
    return <PageLoader title="Organisatie laden..." />;
  }

  if (!selectedOrganization) {
    return (
      <div className="organization-details-page">
        <PageEmpty
          icon={<HiOutlineBuildingOffice2 />}
          title="Organisatie niet gevonden"
          description={
            error || "Deze organisatie bestaat niet of kon niet geladen worden."
          }
        />
      </div>
    );
  }

  return (
    <div className="organization-details-page">
      {/* HEADER */}
      <PageHeader
        eyebrow="POWER"
        title={selectedOrganization.name}
        description="Overzicht van organisatiegegevens en instellingen."
        actions={
          <div className="organization-details-page__header-actions">
            <button
              className="organization-details-page__back-button"
              onClick={() => navigate("/dashboard/organizations")}
            >
              <HiOutlineArrowLeft />

              <span>Terug</span>
            </button>

            <button
              className="organization-details-page__coaches-button"
              onClick={() => navigate(`/dashboard/organizations/${id}/coaches`)}
            >
              <HiOutlineUserGroup />

              <span>Coaches</span>
            </button>

            {can("organization.admin") && (
              <button
                className="organization-details-page__edit-button"
                onClick={() => navigate(`/dashboard/organizations/${id}/edit`)}
              >
                <HiOutlinePencilSquare />

                <span>Bewerken</span>
              </button>
            )}
          </div>
        }
      />

      {/* ERROR */}
      {error && <div className="organization-details-page__error">{error}</div>}

      {/* OVERVIEW */}
      <PageCard className="organization-details-page__overview">
        <div className="organization-details-page__overview-top">
          <div className="organization-details-page__icon">
            <HiOutlineBuildingOffice2 />
          </div>

          <div className="organization-details-page__title">
            <h2>{selectedOrganization.name}</h2>

            <p>
              {selectedOrganization.description ||
                "Geen beschrijving beschikbaar."}
            </p>
          </div>

          <div
            className={`organization-details-page__status ${
              selectedOrganization.is_active
                ? "organization-details-page__status--active"
                : "organization-details-page__status--inactive"
            }`}
          >
            {selectedOrganization.is_active ? (
              <>
                <HiOutlineCheckCircle />
                <span>Actief</span>
              </>
            ) : (
              <>
                <HiOutlineXCircle />
                <span>Inactief</span>
              </>
            )}
          </div>
        </div>

        <div className="organization-details-page__meta-grid">
          {/* CODE */}
          <div className="organization-details-page__meta-card">
            <div className="organization-details-page__meta-icon">
              <HiOutlineHashtag />
            </div>

            <div>
              <span>Code</span>

              <strong>{selectedOrganization.code}</strong>
            </div>
          </div>

          {/* CREATED */}
          <div className="organization-details-page__meta-card">
            <div className="organization-details-page__meta-icon">
              <HiOutlineCalendarDays />
            </div>

            <div>
              <span>Aangemaakt op</span>

              <strong>
                {selectedOrganization.createdAt
                  ? new Date(selectedOrganization.createdAt).toLocaleDateString(
                      "nl-BE",
                    )
                  : "-"}
              </strong>
            </div>
          </div>

          {/* UPDATED */}
          <div className="organization-details-page__meta-card">
            <div className="organization-details-page__meta-icon">
              <HiOutlineCalendarDays />
            </div>

            <div>
              <span>Laatst bijgewerkt</span>

              <strong>
                {selectedOrganization.updatedAt
                  ? new Date(selectedOrganization.updatedAt).toLocaleDateString(
                      "nl-BE",
                    )
                  : "-"}
              </strong>
            </div>
          </div>
        </div>
      </PageCard>
    </div>
  );
}
